import React, { useState, useEffect } from 'react';
import { OPC_DINERO } from '../utils/dinero';

const ESTADOS = {
  borrador: { label: 'Borrador', color: '#888', fondo: '#f5f5f5' },
  pendiente_envio: { label: 'Pendiente de enviar', color: '#5c9adb', fondo: '#f0f4ff' },
  enviada: { label: 'Enviada', color: '#5cd4b0', fondo: '#f0faf6' },
  seguimiento_1: { label: 'Seguimiento 5 días', color: '#dcde74', fondo: '#fafff0' },
  seguimiento_2: { label: 'Seguimiento 10 días', color: '#f4a261', fondo: '#fff8f0' },
  por_vencer: { label: 'Por vencer', color: '#e05c5c', fondo: '#fff0f0' },
  vencida: { label: 'Vencida', color: '#999', fondo: '#f5f5f5' },
  ganada: { label: 'Ganada ✓', color: '#5cd4b0', fondo: '#f0faf6' },
  perdida: { label: 'Perdida', color: '#e05c5c', fondo: '#fff0f0' },
};

const COLORES = {
  principal: '#5c9adb',
  oscuro: '#2f5f9e',
  verde: '#5cd4b0',
  rojo: '#e05c5c',
  amarillo: '#dcde74',
  fondo: '#f3f4f4',
};

const STORAGE_KEY = 'beep_cotizaciones_pipeline';

function diasDesde(fecha) {
  if (!fecha) return 0;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000);
}

function calcularEstado(cot) {
  if (cot.estado === 'ganada' || cot.estado === 'perdida' || cot.estado === 'borrador')
    return cot.estado;
  if (!cot.fechaEnvio) return 'pendiente_envio';
  const dias = diasDesde(cot.fechaEnvio);
  if (dias >= 15) return 'vencida';
  if (dias >= 10) return 'por_vencer';
  if (dias >= 10) return 'seguimiento_2';
  if (dias >= 5) return 'seguimiento_1';
  return 'enviada';
}

export default function PipelineCotizaciones({ rol, nombre, onNavegar, compacto = false }) {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [filtro, setFiltro] = useState('activas');
  const [modalDetalle, setModalDetalle] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      setCotizaciones(JSON.parse(raw));
    } else {
      const ejemplos = [
        {
          id: 'COT-2026-001',
          cliente: 'Bodegas Alianza',
          sucursal: 'Sucursal Norte',
          descripcion: 'Sistema CCTV 16 cámaras IP',
          monto: 121270.42,
          vendedor: 'Ricardo Herrera',
          fechaCreacion: new Date(Date.now() - 2 * 86400000).toISOString(),
          fechaEnvio: new Date(Date.now() - 2 * 86400000).toISOString(),
          estado: 'enviada',
          notas: [],
          historial: [
            { fecha: new Date(Date.now() - 2 * 86400000).toISOString(), accion: 'Cotización creada' },
            { fecha: new Date(Date.now() - 2 * 86400000).toISOString(), accion: 'Enviada al cliente por WhatsApp' },
          ]
        },
        {
          id: 'COT-2026-002',
          cliente: 'Farma Plus',
          sucursal: 'Polanco',
          descripcion: 'Red WiFi mesh 3 puntos de acceso',
          monto: 18500,
          vendedor: 'Ricardo Herrera',
          fechaCreacion: new Date(Date.now() - 7 * 86400000).toISOString(),
          fechaEnvio: new Date(Date.now() - 6 * 86400000).toISOString(),
          estado: 'enviada',
          notas: [],
          historial: [
            { fecha: new Date(Date.now() - 7 * 86400000).toISOString(), accion: 'Cotización creada' },
            { fecha: new Date(Date.now() - 6 * 86400000).toISOString(), accion: 'Enviada al cliente' },
          ]
        },
        {
          id: 'COT-2026-003',
          cliente: 'Miguel Angel Mercado',
          sucursal: 'Local 60',
          descripcion: 'Kit CCTV básico 4 cámaras',
          monto: 13339.22,
          vendedor: 'Ricardo Herrera',
          fechaCreacion: new Date(Date.now() - 0 * 86400000).toISOString(),
          fechaEnvio: null,
          estado: 'pendiente_envio',
          notas: [],
          historial: [
            { fecha: new Date().toISOString(), accion: 'Cotización creada por Axel' },
          ]
        },
      ];
      setCotizaciones(ejemplos);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ejemplos));
    }
  }, []);

  const guardar = (lista) => {
    setCotizaciones(lista);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  };

  const marcarEnviada = (id) => {
    const nueva = cotizaciones.map(c => {
      if (c.id !== id) return c;
      const ahora = new Date().toISOString();
      return {
        ...c,
        fechaEnvio: ahora,
        estado: 'enviada',
        historial: [...(c.historial || []), { fecha: ahora, accion: 'Enviada al cliente' }]
      };
    });
    guardar(nueva);
  };

  const marcarGanada = (id) => {
    const nueva = cotizaciones.map(c => {
      if (c.id !== id) return c;
      const ahora = new Date().toISOString();
      return {
        ...c,
        estado: 'ganada',
        historial: [...(c.historial || []), { fecha: ahora, accion: '¡Cotización ganada! 🎉' }]
      };
    });
    guardar(nueva);
  };

  const marcarPerdida = (id) => {
    const nueva = cotizaciones.map(c => {
      if (c.id !== id) return c;
      const ahora = new Date().toISOString();
      return {
        ...c,
        estado: 'perdida',
        historial: [...(c.historial || []), { fecha: ahora, accion: 'Marcada como perdida' }]
      };
    });
    guardar(nueva);
  };

  const agregarNota = (id, texto) => {
    const nueva = cotizaciones.map(c => {
      if (c.id !== id) return c;
      const ahora = new Date().toISOString();
      return {
        ...c,
        notas: [...(c.notas || []), { fecha: ahora, texto, autor: nombre }],
        historial: [...(c.historial || []), { fecha: ahora, accion: `Nota: ${texto}` }]
      };
    });
    guardar(nueva);
  };

  const cotizacionesConEstado = cotizaciones.map(c => ({
    ...c,
    estadoCalculado: calcularEstado(c),
    diasTranscurridos: c.fechaEnvio ? diasDesde(c.fechaEnvio) : null,
  }));

  const alertas = cotizacionesConEstado.filter(c =>
    ['seguimiento_1', 'seguimiento_2', 'por_vencer'].includes(c.estadoCalculado)
  );

  const filtradas = cotizacionesConEstado.filter(c => {
    if (filtro === 'activas') return !['ganada', 'perdida', 'vencida'].includes(c.estadoCalculado);
    if (filtro === 'alertas') return alertas.some(a => a.id === c.id);
    if (filtro === 'ganadas') return c.estadoCalculado === 'ganada';
    if (filtro === 'todas') return true;
    return true;
  });

  if (compacto) {
    return (
      <div style={{ padding: '12px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORES.oscuro }}>
            💼 Cotizaciones activas
          </div>
          {alertas.length > 0 && (
            <div style={{ background: COLORES.rojo, color: 'white', borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
              {alertas.length} alerta{alertas.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
        {filtradas.slice(0, 3).map(c => {
          const est = ESTADOS[c.estadoCalculado] || ESTADOS.enviada;
          return (
            <div key={c.id} style={{ background: est.fondo, borderLeft: `3px solid ${est.color}`, borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{c.cliente}</div>
                <div style={{ fontSize: 11, color: est.color, fontWeight: 600 }}>{est.label}</div>
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{c.descripcion}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {c.diasTranscurridos !== null ? `${c.diasTranscurridos} días desde envío` : 'Sin enviar'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORES.oscuro }}>
                  ${c.monto?.toLocaleString('es-MX', OPC_DINERO)}
                </div>
              </div>
            </div>
          );
        })}
        {filtradas.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 13, padding: '10px 0' }}>
            No hay cotizaciones activas 🎉
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 16, background: COLORES.fondo, minHeight: '100vh' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: COLORES.oscuro, marginBottom: 16 }}>
        💼 Pipeline de Cotizaciones
      </div>

      {alertas.length > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #dcde74', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: '#856404', fontSize: 14 }}>
            ⚠️ {alertas.length} cotización{alertas.length > 1 ? 'es' : ''} requiere{alertas.length === 1 ? '' : 'n'} seguimiento
          </div>
          {alertas.map(a => (
            <div key={a.id} style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
              • {a.cliente} — {a.diasTranscurridos} días sin respuesta
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['activas', 'alertas', 'ganadas', 'todas'].map(f => (
          <button
            key={f}
            type="button"
            onClick={() => setFiltro(f)}
            style={{
              padding: '6px 14px',
              borderRadius: 99,
              border: 'none',
              background: filtro === f ? COLORES.principal : 'white',
              color: filtro === f ? 'white' : '#666',
              fontSize: 13,
              fontWeight: filtro === f ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {f === 'activas' ? 'Activas' : f === 'alertas' ? `⚠️ Alertas (${alertas.length})` : f === 'ganadas' ? 'Ganadas' : 'Todas'}
          </button>
        ))}
      </div>

      {filtradas.map(c => {
        const est = ESTADOS[c.estadoCalculado] || ESTADOS.enviada;
        return (
          <div key={c.id} style={{ background: 'white', borderRadius: 12, padding: 14, marginBottom: 12, borderLeft: `4px solid ${est.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>{c.id}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#222' }}>{c.cliente}</div>
                <div style={{ fontSize: 13, color: '#666' }}>{c.sucursal} · {c.descripcion}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORES.oscuro }}>${c.monto?.toLocaleString('es-MX', OPC_DINERO)}</div>
                <div style={{ fontSize: 11, background: est.fondo, color: est.color, borderRadius: 99, padding: '2px 8px', fontWeight: 600, marginTop: 4 }}>
                  {est.label}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: '#888' }}>
              <div>
                👤 {c.vendedor} ·
                {c.diasTranscurridos !== null
                  ? ` ${c.diasTranscurridos} días desde envío`
                  : ' Sin enviar'}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {c.estadoCalculado === 'pendiente_envio' && (
                  <button type="button" onClick={() => marcarEnviada(c.id)}
                    style={{ background: COLORES.verde, color: 'white', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                    ✓ Enviada
                  </button>
                )}
                {['enviada', 'seguimiento_1', 'seguimiento_2', 'por_vencer'].includes(c.estadoCalculado) && (
                  <>
                    <button type="button" onClick={() => marcarGanada(c.id)}
                      style={{ background: COLORES.verde, color: 'white', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                      🎉 Ganada
                    </button>
                    <button type="button" onClick={() => marcarPerdida(c.id)}
                      style={{ background: COLORES.rojo, color: 'white', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                      ✗ Perdida
                    </button>
                  </>
                )}
                <button type="button" onClick={() => setModalDetalle(c)}
                  style={{ background: '#f0f4ff', color: COLORES.principal, border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                  Ver
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {filtradas.length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', padding: 40, fontSize: 15 }}>
          No hay cotizaciones en esta vista
        </div>
      )}

      {modalDetalle && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setModalDetalle(null)}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#2f5f9e', marginBottom: 4 }}>{modalDetalle.id}</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{modalDetalle.cliente}</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>{modalDetalle.descripcion}</div>

            <div style={{ fontSize: 13, fontWeight: 600, color: '#2f5f9e', marginBottom: 8 }}>Historial</div>
            {(modalDetalle.historial || []).map((h, i) => (
              <div key={i} style={{ fontSize: 12, color: '#666', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#888' }}>{new Date(h.fecha).toLocaleDateString('es-MX')} </span>
                {h.accion}
              </div>
            ))}

            <div style={{ marginTop: 16 }}>
              <NuevaNotaInput onAgregar={(texto) => {
                agregarNota(modalDetalle.id, texto);
                setModalDetalle(prev => ({
                  ...prev,
                  historial: [...(prev.historial || []), { fecha: new Date().toISOString(), accion: `Nota: ${texto}` }]
                }));
              }} />
            </div>

            <button type="button" onClick={() => setModalDetalle(null)}
              style={{ width: '100%', marginTop: 16, padding: 12, background: '#2f5f9e', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NuevaNotaInput({ onAgregar }) {
  const [texto, setTexto] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        value={texto}
        onChange={e => setTexto(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && texto.trim()) { onAgregar(texto.trim()); setTexto(''); }}}
        placeholder="Agregar nota de seguimiento..."
        style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
      />
      <button type="button" onClick={() => { if (texto.trim()) { onAgregar(texto.trim()); setTexto(''); }}}
        style={{ padding: '8px 14px', background: '#5c9adb', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
        +
      </button>
    </div>
  );
}
